/**
 * Settings — the web-feature migration's first landing (item AN), following the
 * live `dev.bimgo.ai/settings` layout adapted to 2.5's stack (no shadcn/i18n
 * libraries; cards styled in `brand.css`, strings via the local `LocaleProvider`).
 *
 * Role-gated tabs, exactly as the web client decides them: the **Organization**
 * and **Billing & Plan** tabs show only to holders of `org:manage_members`;
 * everyone else sees just the **User** panel with no tab bar. Frontend gating is
 * UX — api-v2 is authoritative, and answers 403 to anyone who reaches the
 * endpoints another way.
 *
 * User tab: role card (+ view permissions), update password, language, sign out,
 * and the desktop build identity. Deferred: the agent-timeout card (needs the
 * agent settings route). Organization tab: the members table with role editing.
 * Billing & Plan tab (#64): the wallet — subscription, credit balance, plan and
 * org cap, per-member spend limits, and the purchases ledger.
 */
import { useState, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { supabase } from "../lib/supabaseClient";
import { isConfigured } from "../lib/config";
import { useLocale } from "../i18n/LocaleProvider";
import { LOCALES, type Locale } from "../i18n/strings";
import { hasAction, useUserPermissions } from "../hooks/usePermissions";
import { RoleBadge } from "../components/settings/RoleBadge";
import { PermissionsDialog } from "../components/settings/PermissionsDialog";
import { OrgMembersTable } from "../components/settings/OrgMembersTable";
import { Spinner } from "../components/settings/Spinner";
import { SettingsIcon } from "../components/settings/SettingsIcon";
import { AgentTimeoutCard } from "../components/settings/AgentTimeoutCard";
import { WalletView } from "../components/wallet/WalletView";
import type { Organization } from "../lib/organizations";

function initials(label: string): string {
  const local = (label.split("@")[0] || label).trim();
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || "?";
}

export default function SettingsPane({
  betaSettings,
  orgId,
  organization,
}: {
  betaSettings?: React.ReactNode;
  orgId: string | null;
  /**
   * The org record the shell already fetched. Passed rather than re-fetched:
   * the subscription the wallet's first card shows is on it, and a second
   * `GET /organizations` for a field already in memory is a request the user
   * pays for in latency for nothing.
   */
  organization: Organization | null;
}) {
  const { user, session } = useAuth();
  const { t, locale, setLocale } = useLocale();

  const token = session?.access_token ?? null;
  const perms = useUserPermissions(orgId, token);
  const isOrgAdmin = hasAction(perms, "org:manage_members");
  const canEditRoles = hasAction(perms, "org:manage_roles");
  // Reads are `org:read`; only the two limit editors need `org:manage_limits`,
  // so an admin who may look but not spend still sees the whole tab, minus the
  // Edit buttons.
  const canManageLimits = hasAction(perms, "org:manage_limits");

  const [tab, setTab] = useState<"user" | "organization" | "wallet">("user");
  const [showPermissions, setShowPermissions] = useState(false);

  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function onUpdatePassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const { error } = await supabase().auth.updateUser({ password });
      if (error) setMessage({ kind: "err", text: error.message });
      else {
        setMessage({ kind: "ok", text: t("updatePassword.successDesc") });
        setPassword("");
      }
    } catch (e) {
      setMessage({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const myRoles = perms.kind === "loaded" ? perms.permissions.roles.org : [];
  const myActions = perms.kind === "loaded" ? perms.permissions.actions : [];
  const email = user?.email ?? t("settings.pane.notSignedIn");

  return (
    <section data-pane="settings">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2>{t("settings.title")}</h2>
        </div>
      </div>

      {isOrgAdmin ? (
        <div className="settings-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "user"}
            className={`settings-tab ${tab === "user" ? "is-active" : ""}`}
            onClick={() => setTab("user")}
          >
            {t("settings.section.user")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "organization"}
            className={`settings-tab ${tab === "organization" ? "is-active" : ""}`}
            onClick={() => setTab("organization")}
          >
            {t("settings.section.organization")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "wallet"}
            className={`settings-tab ${tab === "wallet" ? "is-active" : ""}`}
            onClick={() => setTab("wallet")}
          >
            {t("settings.section.wallet")}
          </button>
        </div>
      ) : null}

      {tab === "organization" && isOrgAdmin && orgId ? (
        <OrgMembersTable organizationId={orgId} token={token} canEditRoles={canEditRoles} />
      ) : tab === "wallet" && isOrgAdmin && orgId ? (
        <WalletView
          organization={organization}
          orgId={orgId}
          token={token}
          canManageLimits={canManageLimits}
        />
      ) : (
        <>
          {/* Account / role */}
          <div className="settings-card">
            <div className="account-row">
              <span className="avatar-lg">{initials(email)}</span>
              <div className="account-meta">
                <div className="account-email">{email}</div>
                <div className="account-badges">
                  {perms.kind === "loading" ? (
                    <Spinner size={14} />
                  ) : myRoles.length > 0 ? (
                    <RoleBadge roles={myRoles} />
                  ) : perms.kind === "loaded" ? (
                    <span className="settings-note">{t("settings.user.roles.none")}</span>
                  ) : null}
                  {myActions.length > 0 ? (
                    <button
                      type="button"
                      className="chip-btn"
                      onClick={() => setShowPermissions(true)}
                    >
                      {t("settings.user.roles.viewPermissions")}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="settings-card">
            <h3 className="settings-card-title"><SettingsIcon name="language" />{t("settings.user.language.title")}</h3>
            {/* The web switches instantly; the desktop shell's own prompts (which only appear at
                launch) pick the new language up on the next start. Shown only in the desktop. */}
            {typeof window !== "undefined" && window.bimgo ? (
              <p className="settings-note settings-description">{t("settings.user.language.restartNote")}</p>
            ) : null}
            <select
              className="settings-input"
              style={{ maxWidth: "12rem" }}
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              aria-label={t("settings.user.language.title")}
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {t(`settings.user.language.${l}`)}
                </option>
              ))}
            </select>
          </div>

          {/* The agent's command timeout — the old web's card, on the agent's
              settings route (item BH5). */}
          <AgentTimeoutCard token={token} />

          {/* Password */}
          <div className="settings-card">
            <h3 className="settings-card-title"><SettingsIcon name="password" />{t("updatePassword.title")}</h3>
            <p className="settings-note settings-description">{t("updatePassword.subtitle")}</p>
            {isConfigured() ? (
              <form className="settings-form" onSubmit={onUpdatePassword}>
                <input
                  className="settings-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("updatePassword.placeholder")}
                  minLength={6}
                  required
                  autoComplete="new-password"
                />
                <button className="settings-btn" type="submit" disabled={busy || !password}>
                  {busy ? t("updatePassword.updating") : t("updatePassword.confirm")}
                </button>
              </form>
            ) : (
              <p className="settings-note">{t("updatePassword.unconfigured")}</p>
            )}
            {message ? (
              <p
                className={`settings-msg ${message.kind === "ok" ? "ok" : "err"}`}
                role={message.kind === "err" ? "alert" : "status"}
              >
                {message.text}
              </p>
            ) : null}
          </div>

          {betaSettings}
        </>
      )}

      <PermissionsDialog
        open={showPermissions}
        onClose={() => setShowPermissions(false)}
        title={t("settings.user.actions.title")}
        actions={myActions}
      />
    </section>
  );
}
